import axios from 'axios';
import { API_URL } from '../config';
import { AxiosRequestConfig } from 'axios';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  permission?: string;
}

export interface LetteraAutocompleteItem {
  id: number | string;
  label: string;
  value: any;
}

interface DjangoGenericAutocompleteResponse {
  results: { id: number | string; text: string; selected_text?: string }[];
  pagination?: { more?: boolean };
}

export interface LetteraAutocompleteResult {
  items: LetteraAutocompleteItem[];
  hasMore: boolean;
}


/* ----------------------------- Types ----------------------------- */

export interface Select2ResultItem {
  id: string;
  text: string;
  selected_text?: string;
  // solo per LettereAutocompleteApi:
  fields?: Record<string, any>;
}

export interface Select2Pagination {
  more: boolean;
}

export interface Select2Response {
  results: Select2ResultItem[];
  pagination: Select2Pagination;
}



export const getLettereList = async (token: string, permission: string | null) => {
    const response = await axios.get(`${API_URL}/lettere/`,
        {
            headers: {
            'Authorization': `${token}`,
            },
            permission,
        } as CustomAxiosRequestConfig);
    return response.data;
};


export async function getLettereCategories(token: string, permission: string | null) {
    const  response  = await axios.get(`${API_URL}/lettere/categories/`,
        {
            headers: {
            'Authorization': `${token}`,
            },
            permission,
        } as CustomAxiosRequestConfig);
    return response.data;
};


export async function getLetteraById(token: string, id: number, permission: string | null) {
    const  response  = await axios.get(`${API_URL}/lettere/${id}/`,
        {
            headers: {
            'Authorization': `${token}`,
            },
            permission,
        } as CustomAxiosRequestConfig);
    return response.data;
};

export const getLetteraPreview = async (token: string, id: number, permission: string | null) => {
    const response = await axios.get(`${API_URL}/lettere/letters/${id}/preview/`,
        {
            headers: {
                'Authorization': `${token}`,
            },
            permission,
            responseType: "arraybuffer",
        } as CustomAxiosRequestConfig);
    return response.data;
};


export async function generateLettera(token: string, id: number, payload: any) {
  const url = `${API_URL}/lettere/letters/${id}/generate/`;
  const { data } = await axios.post(url, payload, {
    headers: {
      'Authorization': `${token}`,
      "Content-Type": "application/json",
    },
    withCredentials: false,
  });
  return data;
}

export async function downloadLettera(token: string, id: number, fileName: string) {
  const url = `${API_URL}/lettere/letters/${id}/download/?file=${encodeURIComponent(fileName)}`;
  return axios.get(url, {
    headers: { 'Authorization': `${token}` },
    responseType: 'blob',
    withCredentials: false,
  });
}


export async function searchLetteraAutocomplete(
  token: string,
  {
    letterId,
    fieldPlaceholder,
    query,
    permission,
    signal,
    page = 1,
  }: {
    letterId: number;
    fieldPlaceholder?: string | null;
    query?: string | null;
    permission?: string | null;
    signal?: AbortSignal;
    page?: number;
  }
): Promise<LetteraAutocompleteResult> {
  const url = `${API_URL}/lettere/generic`;

  const response = await axios.get(url, {
    headers: {
      Authorization: `${token}`,
    },
    params: {
      lettera_id: letterId,
      field: fieldPlaceholder ?? undefined,
      q: query ?? undefined,
      page,
    },
    permission,
    signal,
  } as CustomAxiosRequestConfig);

  const data = response.data as DjangoGenericAutocompleteResponse;
  const rawResults = Array.isArray(data.results) ? data.results : [];

  const items: LetteraAutocompleteItem[] = rawResults.map((r) => ({
    id: r.id,
    label: r.selected_text || r.text || String(r.id),
    value: r,
  }));

  const hasMore = !!data.pagination?.more;

  return { items, hasMore };
}


/* ----------------------------- 1) Generic Autocomplete ----------------------------- */
/**
 * Chiama GenericAutocompleteApi (replica DAL GenericAutocomplete).
 *
 * Query params:
 *  - q
 *  - lettera_id
 *  - field (placeholder, es: "d_sede_legale")
 *  - forward (string JSON, es: "{}" oppure "{\"ccnl\":12}")
 *  - page
 *  - page_size
 */
export const lettereGenericAutocomplete = async (
  token: string,
  params: {
    q: string;
    lettera_id: number;
    field: string;
    forward?: string | Record<string, any>;
    page?: number;
    page_size?: number;
  },
  permission: string | null = null,
  signal?: AbortSignal
): Promise<Select2Response> => {
  const response = await axios.get(`${API_URL}/lettere/autocomplete/generic/`, {
    headers: {
      Authorization: `${token}`,
    },
    params: {
      q: params.q ?? "",
      lettera_id: params.lettera_id,
      field: params.field,
      forward:
        typeof params.forward === "string"
          ? params.forward
          : JSON.stringify(params.forward ?? {}),
      page: params.page ?? 1,
      page_size: params.page_size ?? 25,
    },
    signal,
    permission,
  } as CustomAxiosRequestConfig);

  return response.data;
};

/* ----------------------------- 2) Lettere (company/employee/location) Autocomplete ----------------------------- */
/**
 * Chiama LettereAutocompleteApi (replica DAL LettereAutocomplete).
 *
 * Query params:
 *  - model: "company" | "employee" | "location"
 *  - filter_field: "business_name" | "first_name" | "last_name" | "cf" | ...
 *  - q
 *  - page
 *  - page_size
 *  - company_cf (opzionale)
 *
 * Response: { results: [{id,text,selected_text,fields}], pagination: {more} }
 */
export const lettereAutocomplete = async (
  token: string,
  params: {
    model: string;
    filter_field: string;
    q: string;
    page?: number;
    page_size?: number;
    company_cf?: string;
  },
  permission: string | null = null,
  signal?: AbortSignal
): Promise<Select2Response> => {
  const response = await axios.get(`${API_URL}/lettere/autocomplete/anagrafica/`, {
    headers: {
      Authorization: `${token}`,
    },
    params: {
      model: params.model,
      filter_field: params.filter_field,
      q: params.q ?? "",
      page: params.page ?? 1,
      page_size: params.page_size ?? 25,
      company_cf: params.company_cf,
    },
    signal,
    permission,
  } as CustomAxiosRequestConfig);

  return response.data;
};



export interface AnagraficaAutocompleteItem {
  id: string | number;
  text: string;
  fields?: Record<string, any>;
}

export interface AnagraficaAutocompleteResponse {
  results: AnagraficaAutocompleteItem[];
  pagination: {
    more: boolean;
  };
}

export const lettereAnagraficaAutocomplete = async (
  token: string,
  params: {
    model: string;
    filter_field: string;
    q?: string;
    page?: number;
    page_size?: number;
    company_cf?: string;
  },
  permission: string | null = null,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${API_URL}/anagrafiche/ajax/lettere/`, {
    headers: {
      Authorization: `${token}`,
    },
    params: {
      model: params.model,
      filter_field: params.filter_field,
      q: params.q ?? "",
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      company_cf: params.company_cf,
    },
    permission,
    signal,
  } as CustomAxiosRequestConfig);

  return response.data;
};


export interface LetterCustomTemplatePresence {
  uomo: boolean;
  donna: boolean;
}

export type LetterTemplateSex = "M" | "F";
export function getExampleTemplateUrl(letterId: number | string): string {
  return `${API_URL}/lettere/letters/${letterId}/custom-template/example/`;
}

export function getExistingTemplateUrl(
  letterId: number | string,
  sesso: LetterTemplateSex
): string {
  return `${API_URL}/lettere/letters/${letterId}/custom-template/existing/${sesso}/`;
}

export async function uploadLetterCustomTemplates(
  token: string,
  letterId: number | string,
  formData: FormData,
  permission: string | null = null
): Promise<any> {
  const response = await axios.post(
    `${API_URL}/lettere/letters/${letterId}/custom-template/upload/`,
    formData,
    {
      headers: {
        Authorization: `${token}`,
        "Content-Type": "multipart/form-data",
      },
      permission,
    } as CustomAxiosRequestConfig
  );

  return response.data;
}

export async function downloadExampleTemplate(
  token: string,
  letterId: number | string
) {
  return axios.get(
    `${API_URL}/lettere/letters/${letterId}/custom-template/example/`,
    {
      headers: {
        Authorization: `${token}`,
      },
      responseType: "blob",
    }
  );
}

export async function downloadExistingTemplate(
  token: string,
  letterId: number | string,
  sesso: LetterTemplateSex
) {
  return axios.get(
    `${API_URL}/lettere/letters/${letterId}/custom-template/existing/${sesso}/`,
    {
      headers: {
        Authorization: `${token}`,
      },
      responseType: "blob",
    }
  );
}

export async function getLetterCustomTemplateStatus(
  token: string,
  letterId: number | string,
  permission: string | null = null
) {
  const response = await axios.get(
    `${API_URL}/lettere/letters/${letterId}/custom-template/status/`,
    {
      headers: {
        Authorization: `${token}`,
      },
      permission,
    } as CustomAxiosRequestConfig
  );

  return response.data;
}
